import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OpportunityCard } from "@/components/OpportunityCard";
import { ModeBadge } from "@/components/Brand";
import { priorityStyles } from "@/lib/format";
import { Crosshair, Search, Loader2, List, Map as MapIcon, Download, Sparkles, ChevronDown, Info, SearchX, ServerCrash, KeyRound, MapPinOff, MapPin } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Find salons in Pune with strong reviews but weak websites",
  "Find restaurants in Mumbai that could benefit from WhatsApp automation",
  "Find dentists in Bengaluru with strong demand but poor conversion",
  "Find gyms near 411045 within 5 km",
];
const CATEGORIES = ["Salon", "Restaurant", "Gym", "Dentist", "Cafe", "Jewellery", "Boutique", "Bakery", "Hotel", "Clinic", "Interior Designer", "Coaching Institute", "Automobile", "Real Estate"];

function MiniMap({ opps, onSelect }) {
  const pts = opps.filter((o) => o.lat != null && o.lng != null);
  const lats = pts.map((o) => o.lat), lngs = pts.map((o) => o.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const pos = (o) => ({
    left: `${8 + ((o.lng - minLng) / (maxLng - minLng || 1)) * 84}%`,
    top: `${88 - ((o.lat - minLat) / (maxLat - minLat || 1)) * 76}%`,
  });
  return (
    <div className="lf-card relative overflow-hidden h-[520px] lf-grid" data-testid="map-view">
      <div className="absolute inset-0 lf-radial" />
      {pts.map((o) => {
        const s = priorityStyles(o.priority_label);
        return (
          <button key={o.id} className="absolute -translate-x-1/2 -translate-y-1/2 group" style={pos(o)} data-testid={`map-marker-${o.id}`} onClick={() => onSelect(o)}>
            <span className="block w-3.5 h-3.5 rounded-full ring-4 ring-black/30" style={{ background: s.dot }} />
            <span className="absolute left-1/2 -translate-x-1/2 mt-1 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity text-[11px] bg-slate-900 border border-slate-700 rounded px-2 py-1 z-10 pointer-events-none">
              <span className="font-semibold text-slate-100">{o.name}</span> · {o.opportunity_score} · {o.primary_service}
            </span>
          </button>
        );
      })}
      <div className="absolute bottom-3 left-3 text-[11px] font-mono text-slate-500">Markers by priority · click to open</div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return <div className={className}><p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">{label}</p>{children}</div>;
}

function EmptyState({ result, filters, onDemo, onSwitchDemo }) {
  const status = result?.status;
  const map = {
    demo_no_match: {
      icon: MapPinOff, tone: "amber",
      title: "No demo businesses for this search",
      body: result?.demo_notice ||
        "Demo Mode uses a small curated dataset and doesn't cover this location or category yet.",
      cta: "Try a demo scenario (Pune · Salon)",
    },
    live_no_results: {
      icon: SearchX, tone: "slate",
      title: "No businesses found",
      body: result?.fallback_reason ||
        "The live provider returned no businesses for this PIN code and category. Try a wider radius, a nearby area, or a different category.",
    },
    live_unavailable: {
      icon: ServerCrash, tone: "amber",
      title: "Live search unavailable",
      body: result?.fallback_reason || "All live data providers are currently unavailable. You can switch to Demo Mode to keep exploring with sample data.",
      switchDemo: true,
    },
    not_configured: {
      icon: KeyRound, tone: "amber",
      title: "Live search not configured",
      body: "No live data provider is configured. Switch to Demo Mode to explore sample data, or add a provider key in the backend to enable global live search.",
      switchDemo: true,
    },
  };
  const cfg = map[status] || {
    icon: SearchX, tone: "slate", title: "No opportunities to show",
    body: "Adjust your market filters and search again.",
  };
  const Icon = cfg.icon;
  const toneCls = cfg.tone === "amber" ? "text-amber-400 bg-amber-500/10 border-amber-500/20" : "text-slate-400 bg-slate-800/60 border-slate-700";
  return (
    <div className="lf-card p-10 text-center" data-testid="opportunities-empty-state" data-status={status || "none"}>
      <div className={`w-14 h-14 rounded-2xl grid place-items-center mx-auto mb-4 border ${toneCls}`}>
        <Icon className="w-7 h-7" />
      </div>
      <h3 className="font-display text-lg font-semibold text-slate-100">{cfg.title}</h3>
      <p className="text-sm text-slate-400 mt-2 max-w-lg mx-auto">{cfg.body}</p>
      {status === "demo_no_match" && result?.available_demo && (
        <p className="text-xs text-slate-500 mt-3 max-w-lg mx-auto">
          Demo covers <span className="text-slate-300">{result.available_demo.categories.join(", ")}</span> in{" "}
          <span className="text-slate-300">{result.available_demo.cities.join(", ")}</span>.
        </p>
      )}
      {cfg.switchDemo && (
        <Button className="mt-5 bg-primary hover:bg-emerald-600 text-white" data-testid="switch-to-demo-btn" onClick={onSwitchDemo}>
          Switch to Demo Mode
        </Button>
      )}
      {cfg.cta && (
        <Button className="mt-5 bg-primary hover:bg-emerald-600 text-white" data-testid="empty-demo-cta" onClick={onDemo}>
          {cfg.cta}
        </Button>
      )}
    </div>
  );
}

export default function Opportunities() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [f, setF] = useState({ city: profile?.city || "", country: profile?.country || "", pincode: profile?.pincode || "", area: "", radius_km: profile?.radius_km || 10, category: profile?.industries?.[0] || "Salon" });
  const [showFilters, setShowFilters] = useState(true);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("list");
  const currency = profile?.currency || "INR";
  const opps = result?.opportunities || [];

  const find = async (q, opts = {}) => {
    setLoading(true);
    try {
      const body = { query: q ?? query, ...opts };
      if (!opts.skipFilters) Object.assign(body, { city: f.city || null, country: f.country || null, pincode: f.pincode || null, area: f.area || null, radius_km: Number(f.radius_km) || null, category: f.category || null });
      delete body.skipFilters;
      const { data } = await api.post("/find-clients", body);
      setResult(data);
      if (opts.skipFilters && data.filters) {
        const p = data.filters;
        setF((prev) => ({ ...prev, city: p.city ?? prev.city, country: p.country ?? prev.country, pincode: p.pincode ?? prev.pincode, area: p.area ?? prev.area, radius_km: p.radius_km ?? prev.radius_km, category: p.category ?? prev.category }));
      }
      if (data.status === "live_no_results") toast.warning("No live businesses found — try a wider radius or different category.");
      else if (data.status === "live_unavailable" && data.fallback_reason) toast.warning(data.fallback_reason);
      else if (data.mode === "demo" && data.demo_notice) toast.warning(data.demo_notice.slice(0, 120) + (data.demo_notice.length > 120 ? "…" : ""));
    } catch (e) {
      toast.error("Search failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { find("", { skipFilters: true }); }, []); // eslint-disable-line

  const exportCsv = () => {
    const token = localStorage.getItem("lf_token");
    fetch(`${API}/export.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = "leadforge_opportunities.csv"; a.click();
        URL.revokeObjectURL(url);
        toast.success("CSV exported");
      });
  };

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const meta = result?.meta || {};

  return (
    <div className="space-y-6">
      <div className="lf-card p-6 relative overflow-hidden lf-radial">
        <div className="absolute inset-0 lf-grid opacity-30" />
        <div className="relative max-w-4xl">
          <div className="flex items-center justify-between gap-3">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-50">Find my best clients</h1>
            {result && <ModeBadge mode={result.mode} testid="search-mode-badge" />}
          </div>
          <p className="text-sm text-slate-400 mt-1.5">Set your market below, or describe your ideal prospect in plain English. Discover → score → shortlist → analyse → rank your Top 10.</p>

          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && find(undefined, { skipFilters: true })}
                placeholder="e.g. Find salons in Pune 411001 within 5 km" className="pl-9 h-11 bg-slate-900 border-slate-700" data-testid="nl-search-input" />
            </div>
            <Button onClick={() => find(undefined, query.trim() ? { skipFilters: true } : {})} disabled={loading} className="h-11 px-6 bg-primary hover:bg-emerald-600 text-white" data-testid="find-clients-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Crosshair className="w-4 h-4 mr-2" /> Find my best clients</>)}
            </Button>
          </div>

          <button onClick={() => setShowFilters((s) => !s)} className="mt-3 text-[11px] font-mono uppercase tracking-wider text-slate-400 hover:text-primary flex items-center gap-1" data-testid="toggle-filters-btn">
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilters ? "rotate-180" : ""}`} /> Market filters
          </button>
          {showFilters && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="market-filters">
              <Field label="Category">
                <Select value={f.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="filter-category" className="h-9 bg-slate-900 border-slate-700 text-slate-200 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="text-sm">{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="City"><Input value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Pune / Sydney / Austin" className="h-9 bg-slate-900 border-slate-700" data-testid="filter-city" /></Field>
              <Field label="Country"><Input value={f.country} onChange={(e) => set("country", e.target.value)} placeholder="India / Australia / USA" className="h-9 bg-slate-900 border-slate-700" data-testid="filter-country" /></Field>
              <Field label="Area / ZIP / PIN"><Input value={f.pincode} onChange={(e) => set("pincode", e.target.value)} placeholder="411057 / 2000 / 78701" className="h-9 bg-slate-900 border-slate-700" data-testid="filter-pincode" /></Field>
              <Field label="Radius (km)"><Input type="number" min={1} max={50} value={f.radius_km} onChange={(e) => set("radius_km", e.target.value)} aria-label="Search radius in kilometres" className="h-9 bg-slate-900 border-slate-700" data-testid="filter-radius" /></Field>
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} className="text-[11px] px-2.5 py-1 rounded-md bg-slate-800/60 border border-slate-700 text-slate-400 hover:text-primary hover:border-primary/30 flex items-center gap-1" data-testid={`suggestion-${s.slice(0, 12)}`} onClick={() => { setQuery(s); find(s, { skipFilters: true }); }}>
                <Sparkles className="w-3 h-3" /> {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {result && (
        <div className={`rounded-xl border px-4 py-3 flex flex-col gap-2 text-xs ${result.mode === "live" ? "bg-emerald-500/5 border-emerald-500/20" : "bg-amber-500/5 border-amber-500/20"}`} data-testid="search-status">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <ModeBadge mode={result.mode} testid="status-mode-badge" />
            <span className="text-slate-300">
              {result.mode === "live"
                ? <>Real businesses from <span className="text-slate-100 font-semibold">{result.provider_label || "live provider"}</span> · {result.filters?.category} in {result.filters?.area ? `${result.filters.area}, ` : ""}{result.filters?.city || result.filters?.pincode}</>
                : <>Curated demo dataset · clearly labelled, never mixed with live data</>}
            </span>
            <span className="sm:ml-auto flex items-center gap-1.5 font-mono text-slate-500"
              title="All shortlisted businesses are scored & ranked from the live provider's facts. Websites are deep-analysed only where a site is publicly listed — the rest are scored on listing facts alone.">
              {result.mode === "live"
                ? <>Discovered {meta.discovered} · scored &amp; shortlisted {meta.shortlisted} · websites analysed {meta.analyzed} · Top {opps.length} ranked</>
                : <>{opps.length} demo {opps.length === 1 ? "business" : "businesses"} scored &amp; ranked</>}
              {result.mode === "live" && <Info className="w-3.5 h-3.5 text-slate-600" />}
            </span>
          </div>
          {meta.search_area && (
            <div className="flex items-center gap-1.5 text-slate-400" data-testid="search-area">
              <MapPin className="w-3.5 h-3.5 text-primary" />
              Search area: {meta.search_area.count} results within <span className="text-slate-200 font-semibold">~{meta.search_area.max_km} km</span> of {meta.search_area.label}
              <span className="text-slate-600 font-mono">· median ~{meta.search_area.median_km} km · requested radius {meta.search_area.requested_radius_km} km</span>
            </div>
          )}
          {result.fallback_reason && (
            <span className="flex items-center gap-1 text-amber-400" data-testid="fallback-reason"><Info className="w-3.5 h-3.5" /> {result.fallback_reason}</span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-slate-100">Your top {opps.length} {result?.mode === "live" ? "real " : ""}opportunities</h2>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-700 overflow-hidden">
            <button className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${view === "list" ? "bg-slate-800 text-slate-100" : "text-slate-400"}`} data-testid="view-list-btn" onClick={() => setView("list")}><List className="w-4 h-4" /> List</button>
            <button className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${view === "map" ? "bg-slate-800 text-slate-100" : "text-slate-400"}`} data-testid="view-map-btn" onClick={() => setView("map")}><MapIcon className="w-4 h-4" /> Map</button>
          </div>
          <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="export-csv-btn" onClick={exportCsv}><Download className="w-4 h-4 mr-1.5" /> CSV</Button>
        </div>
      </div>

      {result?.demo_notice && opps.length > 0 && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-2.5 flex items-start gap-2 text-xs text-amber-300" data-testid="demo-notice-banner">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {result.demo_notice}
        </div>
      )}

      {loading && !result ? (
        <div className="h-[40vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : loading ? (
        <div className="lf-card p-10 text-center" data-testid="live-progress">
          <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-slate-200 font-medium">Discovering businesses → scoring → shortlisting → analysing websites → ranking…</p>
          <p className="text-xs text-slate-500 mt-1">Live analysis fetches each shortlisted website, so this can take 10–20 seconds.</p>
        </div>
      ) : opps.length === 0 ? (
        <EmptyState result={result} filters={f} onSwitchDemo={() => find(undefined, { mode: "demo" })} onDemo={() => { setQuery(""); find("", { skipFilters: true, city: "Pune", category: "Salon", pincode: "411001", area: null, radius_km: 15 }); }} />
      ) : view === "map" ? (
        <MiniMap opps={opps} onSelect={(o) => navigate(`/app/opportunity/${o.id}`)} />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {opps.map((opp, i) => <OpportunityCard key={opp.id} opp={opp} rank={i + 1} currency={currency} onGeneratePitch={(o) => navigate(`/app/opportunity/${o.id}?pitch=1`)} />)}
        </div>
      )}
    </div>
  );
}
